import React from "react";
import {
  Form,
  Table,
  Checkbox,
  Input,
  Segment,
  Button,
  Message,
} from "semantic-ui-react";
import { Formik, Field, FieldArray } from "formik";
import * as Yup from "yup";

const AuditStep = ({
  supplyUnits,
  setSupplyUnits,
  onStepComplete,
  inventory,
  onPrevious,
}) => {
  // Create validation schema
  const validationSchema = Yup.object().shape({
    units: Yup.object().shape(
      supplyUnits.reduce((acc, unit) => {
        acc[`upc_${unit.upc}`] = Yup.object().shape({
          assign_new_inventory: Yup.boolean(),
          inv: Yup.number()
            .min(0, "Value must be positive or zero")
            .required(`Inventory value is required for UPC ${unit.upc}`),
        });
        return acc;
      }, {})
    ),
  });

  return (
    <Formik
      initialValues={{
        units: supplyUnits.reduce((acc, unit) => {
          // Use the same upc_ prefix for initialValues
          acc[`upc_${unit.upc}`] = {
            assign_new_inventory: unit.assign_new_inventory || false,
            inv: unit.inv || "",
          };
          return acc;
        }, {}),
      }}
      validationSchema={validationSchema}
      validate={(values) => {
        // Extra validation to check if at least one item is selected
        const hasSelection = Object.values(values.units || {}).some(
          (unit) => unit.assign_new_inventory === true
        );

        if (!hasSelection) {
          return {
            units:
              "You must select at least one item to assign a new inventory ID",
          };
        }

        const allSelected = Object.values(values.units || {}).every(
          (unit) => unit.assign_new_inventory === true
        );

        if (allSelected) {
          return {
            units: "Cannot select all units to be reassigned",
          };
        }

        return {}; // No additional errors
      }}
      onSubmit={(values) => {
        // Updating supply unit state with new properties/values
        setSupplyUnits((prev) =>
          prev.map((unit) => {
            const formValues = values.units[`upc_${unit.upc}`];

            return {
              ...unit,
              inv: Number(formValues.inv),
              assign_new_inventory: formValues.assign_new_inventory,
            };
          })
        );
        onStepComplete();
      }}
    >
      {({ isSubmitting, errors, handleSubmit, submitCount }) => (
        <Form onSubmit={handleSubmit}>
          <Segment>
            <p style={{ textAlign: "center", color: "red", marginTop: "10px" }}>
              <b>
                Please audit all inventory and shelf values and check items you
                want to reassign to continue.
              </b>
            </p>

            {/* Display top-level validation error */}
            {errors.units &&
              typeof errors.units === "string" &&
              submitCount > 0 && (
                <Message negative>
                  <Message.Header>Validation Error</Message.Header>
                  <p>{errors.units}</p>
                </Message>
              )}

            {/* Audit Table */}
            <FieldArray name="units">
              {() => (
                <Table celled>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>UPC</Table.HeaderCell>
                      <Table.HeaderCell>Title</Table.HeaderCell>
                      <Table.HeaderCell>Shelf</Table.HeaderCell>
                      <Table.HeaderCell>
                        Assign New Inventory ID?
                      </Table.HeaderCell>
                      <Table.HeaderCell>Inv</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {supplyUnits.map((unit) => (
                      <Table.Row key={unit.upc}>
                        <Table.Cell>{unit.upc}</Table.Cell>
                        <Table.Cell>{unit.supply_title}</Table.Cell>
                        <Table.Cell>{inventory.inventory_shelf}</Table.Cell>
                        <Table.Cell>
                          <Field
                            name={`units.upc_${unit.upc}.assign_new_inventory`}
                          >
                            {({ field }) => (
                              <Checkbox
                                checked={field.value}
                                onChange={(e, data) => {
                                  field.onChange({
                                    target: {
                                      name: field.name,
                                      value: data.checked,
                                    },
                                  });
                                }}
                              />
                            )}
                          </Field>
                        </Table.Cell>
                        <Table.Cell>
                          <Field name={`units.upc_${unit.upc}.inv`}>
                            {({ field, meta }) => (
                              <Input
                                {...field}
                                size="small"
                                fluid
                                error={meta.touched && meta.error !== undefined}
                              />
                            )}
                          </Field>
                          {errors.units?.[`upc_${unit.upc}`]?.inv &&
                            submitCount > 0 && (
                              <span style={{ color: "red" }}>
                                {errors.units[`upc_${unit.upc}`].inv}
                              </span>
                            )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </FieldArray>

            {/* Action Buttons */}
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button type="button" onClick={onPrevious}>
                Cancel
              </Button>
              <Button type="submit" primary disabled={isSubmitting}>
                Next
              </Button>
            </div>
          </Segment>
        </Form>
      )}
    </Formik>
  );
};

export default AuditStep;
